import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Paper,
  useTheme,
  useMediaQuery,
  Modal,
  TextField,
  InputAdornment,
  Slide,
  Switch,
  Fab,
} from "@mui/material";
import {
  Person,
  ShoppingBag,
  Settings,
  Favorite,
  LocationOn,
  Payment,
  Security,
  Notifications,
  Edit,
  Email,
  Numbers,
  Add,
  Business,
  AccountTree,
  AddLocationAlt,
  PinDrop,
  CreditCard,
  Lock,
  Palette,
  Language,
  CalendarToday,
  Phone,
  Menu as MenuIcon,
  ArrowBack
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  get_user_data,
  update_user,
  create_address,
} from "../../services/profileService";
import { useNotification } from "../../contexts/NotificationContext";
import ProfileOrderTable from "../../components/user/ProfileOrderTable";
import ProfileAddressTable from "../../components/user/ProfileAddressTable";

const editModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 3,
  border: "2px solid #680000",
};

const ProfilePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showSuccess } = useNotification();

  const [activeSection, setActiveSection] = useState(location?.state?.section || "profile");
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const navigationItems = [
    { id: "profile", label: "Profile", icon: <Person />, color: "#680000" },
    {
      id: "orders",
      label: "My Orders",
      icon: <ShoppingBag />,
      color: "#8B0000",
    },
    {
      id: "address",
      label: "Address",
      icon: <LocationOn />,
      color: "#A52A2A",
    },
    {
      id: "payments",
      label: "Payment Methods",
      icon: <Payment />,
      color: "#DC143C",
    },
    {
      id: "favorites",
      label: "Favorites",
      icon: <Favorite />,
      color: "#B22222",
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: <Notifications />,
      color: "#CD5C5C",
    },
    { id: "security", label: "Security", icon: <Security />, color: "#F08080" },
    { id: "settings", label: "Settings", icon: <Settings />, color: "#E9967A" },
  ];

  // Speed dial actions for mobile
  const speedDialActions = [
    { icon: <Person />, name: "Profile", id: "profile" },
    { icon: <ShoppingBag />, name: "Orders", id: "orders" },
    { icon: <LocationOn />, name: "Address", id: "address" },
    { icon: <Favorite />, name: "Favorites", id: "favorites" },
    { icon: <Payment />, name: "Payments", id: "payments" },
    { icon: <Notifications />, name: "Notifications", id: "notifications" },
    { icon: <Security />, name: "Security", id: "security" },
    { icon: <Settings />, name: "Settings", id: "settings" },
  ];

  useEffect(() => {
    const currentItem = navigationItems.find(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path)
    );
    if (currentItem) {
      setActiveSection(currentItem.id);
    }
  }, [location.pathname]);

  useEffect(() => {
    async function fetchUserData() {
      try {
        let user = JSON.parse(sessionStorage.getItem("user"));
        // let user_id = parseInt(user['id'])
        const response = await get_user_data();
        setUserData(response);
      } catch (error) {
        // console.error("Error on Getting User => ", error);
        showError(error?.message)
      }
    }
    fetchUserData();
  }, [addressModalOpen]);

  const handleNavigation = (item) => {
    setActiveSection(item.id);
    navigate(item.path);
  };

  const handleSpeedDialNavigation = (id) => {
    const item = navigationItems.find(nav => nav.id === id);
    if (item) {
      handleNavigation(item);
    }
    setSpeedDialOpen(false);
  };

  const isActive = (itemId) => {
    return activeSection === itemId;
  };

  const handleEditUserData = (e) => {
    let updateData = {
      ...userData,
      [e.target.name]: e.target.value,
    };
    setUserData(updateData);
  };

  const updateUserData = async () => {
    if (userData.username === "") {
      showError("Name Cannot be Empty");
      return;
    }
    const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const email_verify = regex.test(userData.email.trim());

    if (userData.email !== "" && !email_verify) {
      showError("Please Enter Valid Email");
      return;
    }

    // if (userData.phone !== "" && !/^\d+$/.test(userData.phone)) {
    //   showError("Phone number must contain only digits");
    //   return;
    // }

    try {
      const response = await update_user(userData);
      showSuccess(response.message);
      setEditModalOpen(false);
    } catch (error) {
      // console.error("Error Happened => ", error);
      showError(error?.message);
      setEditModalOpen(false);
    }
  };

  const renderMainContent = () => {
    switch (activeSection) {
      case "profile":
        return (
          <ProfileSection
            userData={userData}
            isMobile={isMobile}
            editModalOpen={editModalOpen}
            setEditModalOpen={setEditModalOpen}
          />
        );
      case "orders":
        return <OrdersSection isMobile={isMobile} />;
      case "address":
        return (
          <AddressesSection
            isMobile={isMobile}
            addressModalOpen={addressModalOpen}
            setAddressModalOpen={setAddressModalOpen}
          />
        );
      case "payments":
        return <PaymentsSection isMobile={isMobile} />;
      case "favorites":
        return <FavoritesSection isMobile={isMobile} />;
      case "notifications":
        return <NotificationsSection isMobile={isMobile} />;
      case "security":
        return <SecuritySection isMobile={isMobile} />;
      case "settings":
        return <SettingsSection isMobile={isMobile} />;
      default:
        return <ProfileSection userData={userData} isMobile={isMobile} />;
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          py: isMobile ? 1 : 3,
          pb: isMobile ? 8 : 3,
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          mt: isMobile ? 3 : 20, // Reduced top margin for mobile
          mb: 5,
        }}
      >
        <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 3 }}>
          <Grid container spacing={isMobile ? 1 : 3}>
            {!isMobile && (
              <Grid item xs={12} md={3} lg={2.5}>
                <Paper
                  elevation={4}
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    background:
                      "linear-gradient(135deg, #680000 0%, #8B0000 100%)",
                    color: "white",
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: "auto",
                        mb: 2,
                        bgcolor: "rgba(255,255,255,0.2)",
                        fontSize: "2rem",
                        border: "3px solid white",
                      }}
                    >
                      {userData.username?.charAt(0) || "U"}
                    </Avatar>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {userData.username}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.8 }}
                      gutterBottom
                    >
                      {userData.email}
                    </Typography>
                  </Box>

                  <List sx={{ p: 1 }}>
                    {navigationItems.map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => handleNavigation(item)}
                          selected={isActive(item.id)}
                          sx={{
                            borderRadius: 2,
                            mx: 1,
                            py: 1.5,
                            "&.Mui-selected": {
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "white",
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.3)",
                              },
                            },
                            "&:hover": {
                              bgcolor: "rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 40,
                              color: isActive(item.id)
                                ? "white"
                                : "rgba(255,255,255,0.8)",
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontSize: "0.95rem",
                              fontWeight: isActive(item.id) ? 600 : 400,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} md={isMobile ? 12 : 9} lg={isMobile ? 12 : 9.5}>
              <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                <Paper
                  elevation={4}
                  sx={{
                    mt:{xs:"102px",sm:"102px",md:0},
                    borderRadius: 3,
                    border: "none",
                    bgcolor: "background.paper",
                    minHeight: isMobile ? "calc(100vh - 80px)" : "600px", // Adjusted height
                    overflow: "hidden",
                  }}
                >
                  {renderMainContent()}

                  <Modal
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box sx={editModalStyle}>
                      <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ textAlign: "center", mb: 3, color: "#680000" }}
                      >
                        Update Profile
                      </Typography>

                      <TextField
                        fullWidth
                        name="username"
                        label="Name"
                        value={userData.username}
                        onChange={(e) => handleEditUserData(e)}
                        required
                        size="medium"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: "#680000" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: '6px',
                          },
                        }}
                      />

                      <TextField
                        fullWidth
                        name="email"
                        label="Email"
                        value={userData.email}
                        onChange={(e) => handleEditUserData(e)}
                        required
                        size="medium"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: "#680000" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: '6px',
                          },
                        }}
                      />

                      <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Button
                          onClick={updateUserData}
                          variant="contained"
                          sx={{
                            mt:2,
                            bgcolor: "#680000",
                            borderRadius: '6px',
                            px: 4,
                            py: 1,
                            "&:hover": {
                              bgcolor: "#8B0000",
                            },
                          }}
                        >
                          Update Profile
                        </Button>
                      </Box>
                    </Box>
                  </Modal>
                </Paper>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Floating Action Button for Mobile Navigation */}
      {isMobile && (
        <>
          <Fab
            onClick={() => setSpeedDialOpen(!speedDialOpen)}
            sx={{
              position: "fixed",
              bottom: 20,
              right: 20,
              bgcolor: "#680000",
              color: "white",
              zIndex: 1200,
              "&:hover": {
                bgcolor: "#8B0000",
                transform: "scale(1.1)",
              },
              transition: "all 0.3s ease",
              boxShadow: 4,
            }}
            size="large"
          >
            {speedDialOpen ? <Person /> : <MenuIcon />}
          </Fab>

          {/* Speed Dial Menu */}
          {speedDialOpen && (
            <Box
              sx={{
                position: "fixed",
                bottom: 80,
                right: 20,
                zIndex: 1199,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              {speedDialActions.map((action, index) => (
                <Box
                  key={action.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    animation: `slideIn 0.2s ease ${index * 0.05}s forwards`,
                    opacity: 0,
                    transform: "translateX(20px)",
                    "@keyframes slideIn": {
                      to: {
                        opacity: 1,
                        transform: "translateX(0)",
                      },
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      bgcolor: "rgba(104, 0, 0, 0.9)",
                      color: "white",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {action.name}
                  </Typography>
                  <Fab
                    onClick={() => handleSpeedDialNavigation(action.id)}
                    size="small"
                    sx={{
                      bgcolor: activeSection === action.id ? "#8B0000" : "#680000",
                      color: "white",
                      "&:hover": {
                        bgcolor: "#A52A2A",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {action.icon}
                  </Fab>
                </Box>
              ))}
            </Box>
          )}

          {/* Backdrop for closing menu */}
          {speedDialOpen && (
            <Box
              onClick={() => setSpeedDialOpen(false)}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1198,
                bgcolor: "rgba(0, 0, 0, 0.2)",
              }}
            />
          )}
        </>
      )}
    </>
  );
};

const ProfileSection = ({
  userData,
  isMobile,
  editModalOpen,
  setEditModalOpen,
}) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "center",
        mb: 4,
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 2 : 0,
      }}
    >
      <Typography
        variant={isMobile ? "h5" : "h4"}
        fontWeight="700"
        color="#680000"
      >
        Profile Information
      </Typography>
      {/* <Button
        onClick={() => setEditModalOpen(true)}
        variant="contained"
        startIcon={<Edit />}
        size={isMobile ? "small" : "medium"}
        sx={{
          bgcolor: "#680000",
          borderRadius: 2,
          px: 3,
          "&:hover": {
            bgcolor: "#8B0000",
          },
        }}
      >
        Edit Profile
      </Button> */}
    </Box>

    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <Card
          elevation={2}
          sx={{
            height: "100%",
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: 4,
            },
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#680000",
              }}
            >
              <Person /> Personal Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ "& > *:not(:last-child)": { mb: 2 } }}>
              <InfoRow
                label="Full Name"
                value={userData.username}
                icon={<Person />}
              />
              <InfoRow
                label="Email Address"
                value={userData.email}
                icon={<Email />}
              />
              <InfoRow
                label="Phone Number"
                value={userData.phone}
                icon={<Phone />}
              />
              <InfoRow
                label="Member Since"
                value={new Date(userData.created_at).toLocaleDateString('en-GB')}
                icon={<CalendarToday />}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const OrdersSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      My Orders
    </Typography>
    <ProfileOrderTable />
  </Box>
);

const AddressesSection = ({
  isMobile,
  addressModalOpen,
  setAddressModalOpen,
}) => {
  useEffect(() => {}, [addressModalOpen])
  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          fontWeight="700"
          color="#680000"
        >
          My Addresses
        </Typography>
        <Button
          variant="contained"
          onClick={() => setAddressModalOpen(true)}
          startIcon={<Add />}
          sx={{
            bgcolor: "#680000",
            borderRadius: 2,
            px: 3,
            "&:hover": {
              bgcolor: "#8B0000",
            },
          }}
        >
          Add Address
        </Button>
      </Box>
      <ProfileAddressTable addressModalOpen={addressModalOpen}
        setAddressModalOpen={setAddressModalOpen} />
      <AddAddressModal
        addressModalOpen={addressModalOpen}
        setAddressModalOpen={setAddressModalOpen}
      />
    </Box>
  );
};

const PaymentsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      Payment Methods
    </Typography>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, border: "2px solid #680000" }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CreditCard sx={{ fontSize: 48, color: "#680000", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Add Payment Method
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Securely add your credit or debit card
            </Typography>
            <Button
              variant="outlined"
              sx={{ borderColor: "#680000", color: "#680000" }}
            >
              Add Card
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const FavoritesSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      My Favorites
    </Typography>
    <Box sx={{ textAlign: "center", py: 8 }}>
      <Favorite sx={{ fontSize: 64, color: "#680000", mb: 2, opacity: 0.5 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No favorites yet
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Your favorite items will appear here
      </Typography>
    </Box>
  </Box>
);

const NotificationsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      Notifications
    </Typography>
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        {[
          "Order Updates",
          "Promotional Offers",
          "Security Alerts",
          "New Features",
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            <Typography variant="body1">{item}</Typography>
            <Switch defaultChecked color="error" />
          </Box>
        ))}
      </CardContent>
    </Card>
  </Box>
);

const SecuritySection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      Security
    </Typography>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Lock sx={{ color: "#680000" }} />
            <Typography variant="h6">Change Password</Typography>
          </Box>
          <Button variant="contained" sx={{ bgcolor: "#680000" }}>
            Update Password
          </Button>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const SettingsSection = ({ isMobile }) => (
  <Box sx={{ p: isMobile ? 2 : 4 }}>
    <Typography
      variant={isMobile ? "h5" : "h4"}
      fontWeight="700"
      mb={4}
      color="#680000"
    >
      Settings
    </Typography>
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        {["Language", "Theme", "Privacy", "Help & Support"].map(
          (item, index) => (
            <ListItemButton key={index} sx={{ py: 2 }}>
              <ListItemIcon>
                {index === 0 ? (
                  <Language />
                ) : index === 1 ? (
                  <Palette />
                ) : index === 2 ? (
                  <Security />
                ) : (
                  <Settings />
                )}
              </ListItemIcon>
              <ListItemText primary={item} />
              <ArrowBack sx={{ transform: "rotate(180deg)" }} />
            </ListItemButton>
          )
        )}
      </CardContent>
    </Card>
  </Box>
);

const InfoRow = ({ label, value, icon }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="body2" color="text.secondary" fontWeight="500">
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight="600" sx={{ textAlign: "right" }}>
      {value}
    </Typography>
  </Box>
);

const AddAddressModal = ({ addressModalOpen, setAddressModalOpen }) => {
  const { showSuccess, showError } = useNotification();
  const [validationError, setValidationError] = useState({ 'label': "", "error": "" })

  const [form, setForm] = useState({
    address_name: "",
    building_name: "",
    block_number_and_street_name: "",
    unit_number: "",
    pincode: "",
  });

  const modalForm = [
    { id: 1, name: "address_name", label: "Address Name", icon: <Business /> },
    {
      id: 2,
      name: "building_name",
      label: "Building Name",
      icon: <AccountTree />,
    },
    {
      id: 3,
      name: "block_number_and_street_name",
      label: "Block Number and Street Name",
      icon: <Numbers />,
    },
    {
      id: 4,
      name: "unit_number",
      label: "Unit Number",
      icon: <AddLocationAlt />,
    },
    { id: 5, name: "pincode", label: "Pincode", icon: <PinDrop /> },
  ];

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
    border: "2px solid #680000",
  };

  const handleAddressChange = (e) => {
    const updateData = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(updateData);
  };

  const submitAddress = async () => {
    if (form.address_name === "") {
      setValidationError({ 'label': "address_name", "error": "Please Enter Address Name" })
      return;
    }

    if (form.address_name !== "" && form.address_name.length > 70) {
      setValidationError({ 'label': "address_name", "error": "Please Give Address name less than 70 characters" })
      return;
    }

    if (form.building_name === "") {
      setValidationError({ 'label': "building_name", "error": "Please Enter Building Name" })
      return;
    }

    if (form.building_name !== "" && form.building_name.length > 70) {
      setValidationError({ 'label': "building_name", "error": "Please Give Building name less than 70 characters" })
      return;
    }

    if (form.block_number_and_street_name === "") {
      setValidationError({ 'label': "block_number_and_street_name", "error": "Please Enter Building Number" })
      return;
    }

    if (form.block_number_and_street_name !== "" && form.block_number_and_street_name.length > 10) {
      setValidationError({ 'label': "block_number_and_street_name", "error": "Please Give Building Number less than 10 characters" })
      return;
    }

    if (form.unit_number === "") {
      setValidationError({ 'label': "unit_number", "error": "Please Enter Street Address " })
      return;
    }

    if (form.pincode === "") {
      setValidationError({ 'label': "pincode", "error": "Please Enter Pincode" })
      return;
    }

    const isNumberOnly = /^[0-9]+$/.test(form.pincode);
    if (form.pincode !== "") {
      if (!isNumberOnly) {
        setValidationError({ label: "pincode", error: "Pincode must be numeric" });
        return;
      }

      if (form.pincode.length > 6) {
        setValidationError({ label: "pincode", error: "Pincode is too long" });
        return;
      }
    }

    try {
      const response = await create_address(form);
      showSuccess("Address Created Successfully");
      setForm({
        address_name: "",
        building_name: "",
        block_number_and_street_name: "",
        unit_number: "",
        pincode: "",
      });
      setAddressModalOpen(false);
    } catch (error) {
      // console.error("Error on Creating Address");
      showError("Something Went Wrong");
    }

    setValidationError({ 'label': "", "error": "" })
  };

  return (
    <Modal
      open={addressModalOpen}
      onClose={() => {
        setForm({
          address_name: "",
          building_name: "",
          block_number_and_street_name: "",
          unit_number: "",
          pincode: "",
        });
        setAddressModalOpen(false)
        setValidationError({ 'label': "", "error": "" })
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography
          variant="h5"
          sx={{ mb: 3, textAlign: "center", color: "#680000" }}
        >
          Add New Address
        </Typography>
        {modalForm.map((items) => (
          <React.Fragment key={items.id}>
            <TextField
              fullWidth
              name={items.name}
              label={items.label}
              value={form[items.name]}
              onChange={(e) => handleAddressChange(e)}
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{items.icon}</InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
            {validationError.label === items.name && <Typography sx={{ mb: 2, color: "red" }}>*{validationError.error}</Typography>}
          </React.Fragment>
        ))}
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={submitAddress}
            sx={{
              bgcolor: "#680000",
              borderRadius: 2,
              px: 4,
              "&:hover": {
                bgcolor: "#8B0000",
              },
            }}
          >
            Save Address
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProfilePage;